import {
    Button,
    List,
    ListItem,
    makeStyles,
    mergeClasses,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarProps,
    SelectionItemId,
    Spinner,
    tokens,
} from '@fluentui/react-components';
import { DismissRegular, FolderArrowRight20Regular } from '@fluentui/react-icons';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddReturnsToExistingFolder, useCreateFolderWithTaxReturns } from '../../api/report/report';
import { createFilterItem } from '../../data-types/filterItem';
import { useDefaultModalStyles } from '../../hooks/useDefaultModalStyles';
import { useFeedbackMessages } from '../../hooks/useFeedbackMessages';
import { useFunctionalAccess } from '../../hooks/useFunctionalAccess';
import { ReturnFolderDto } from '../../model/returnFolderDto';
import { customPalette } from '../../theme';
import { getFilteredFilterItems } from '../../utils/FilterUtils';
import FunctionalAccessDisableWrapper from '../common/FunctionalAccessDisableWrapper';
import Modal from '../common/ModalV9';
import { useFilterPickerStyles } from '../filters/filter-picker/FilterPicker';
import FilterSearchBar from '../filters/filter-search-bar/FilterSearchBar';
import FolderListRow from './FolderListRow';

export interface AddToFolderModalProps {
    isOpen: boolean;
    folders: ReturnFolderDto[] | undefined;
    selectedReturns: number[];
    getAllFoldersQuery: QueryKey;
    isLoadingGetAllFolders: boolean;
    onClose: () => void;
}

const useStyle = makeStyles({
    modalContentStyles: {
        height: '40vh',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        minHeight: 0,
    },
    searchWrapper: {
        width: '100%',
        // FilterSearchBar hardcodes a popover width; constrain it to the dialog body.
        '& .fui-SearchBox': {
            width: '100%',
            minWidth: '0',
            maxWidth: '100%',
        },
    },
    listRegion: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    list: {
        margin: 0,
        padding: 0,
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        minHeight: '44px',
        color: customPalette.blueDark,
        backgroundColor: 'transparent',
        // Match Form TOC TreeItemLayout / Fluent subtle hover used by the folder filter.
        ':hover': {
            backgroundColor: tokens.colorSubtleBackgroundHover,
        },
        ':active': {
            backgroundColor: tokens.colorSubtleBackgroundPressed,
        },
    },
    listItemSelected: {
        // Match FormTOCControlledTreeView selectedRow.
        backgroundColor: customPalette.statusCoolLightGrey,
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
    duplicateError: {
        color: customPalette.red,
        fontSize: tokens.fontSizeBase200,
        paddingLeft: tokens.spacingHorizontalXS,
    },
    inlineAddRow: {
        width: '100%',
    },
});

const AddToFolderModal: React.FC<AddToFolderModalProps> = ({
    isOpen,
    folders,
    selectedReturns,
    getAllFoldersQuery,
    isLoadingGetAllFolders,
    onClose,
}) => {
    const { t } = useTranslation();
    const styles = useStyle();
    const pickerStyles = useFilterPickerStyles();
    const { button } = useDefaultModalStyles();
    const [selectedFolder, setSelectedFolder] = React.useState<ReturnFolderDto | undefined>(undefined);
    const [isAddingFolder, setIsAddingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [isDuplicateError, setIsDuplicateError] = useState(false);
    const [duplicateErrorMessage, setDuplicateErrorMessage] = useState<string | undefined>(undefined);
    const [messageBar, setMessageBar] = React.useState<React.ReactElement<MessageBarProps> | undefined>(undefined);
    const queryClient = useQueryClient();
    const { displayFeedbackMessage } = useFeedbackMessages();
    const { functionalAccess } = useFunctionalAccess();

    const { mutateAsync: addReturnsToExistingFolder, isLoading: isSubmitting } = useAddReturnsToExistingFolder({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries(getAllFoldersQuery);
            },
        },
    });

    const { mutateAsync: saveNewFolderAndReturns, isLoading: isCreating } = useCreateFolderWithTaxReturns();

    const folderFilterItems = useMemo(
        () =>
            (folders ?? [])
                .filter((folder) => folder.folderKey != null && folder.folderName)
                .map((folder) => createFilterItem(folder.folderName as string, String(folder.folderKey))),
        [folders]
    );

    const visibleFilterItems = useMemo(() => getFilteredFilterItems(folderFilterItems, searchValue), [folderFilterItems, searchValue]);

    const visibleFolders = useMemo(() => {
        const visibleKeys = new Set(visibleFilterItems.map((item) => item.key));
        return (folders ?? []).filter((folder) => folder.folderKey != null && visibleKeys.has(String(folder.folderKey)));
    }, [folders, visibleFilterItems]);

    const hasResults = visibleFolders.length > 0;
    const selectedItems: SelectionItemId[] = selectedFolder?.folderKey != null ? [String(selectedFolder.folderKey)] : [];

    const clearInlineAddState = () => {
        setIsAddingFolder(false);
        setNewFolderName('');
        setIsDuplicateError(false);
        setDuplicateErrorMessage(undefined);
    };

    const clearStateAndClose = () => {
        setSelectedFolder(undefined);
        setMessageBar(undefined);
        setSearchValue('');
        clearInlineAddState();
        onClose();
    };

    const setErrorMessageBar = (message: string) => {
        setMessageBar(
            <MessageBar intent='error'>
                <MessageBarBody>{message}</MessageBarBody>
                <MessageBarActions
                    containerAction={
                        <Button
                            appearance='transparent'
                            icon={<DismissRegular />}
                            aria-label={t('close').toString()}
                            onClick={() => setMessageBar(undefined)}
                        />
                    }
                />
            </MessageBar>
        );
    };

    const headerContent = <>{`${t('addToFolder')} (${selectedReturns.length} ${t('items', { count: selectedReturns.length })})`}</>;

    const handleStartAddFolder = () => {
        if (isAddingFolder || isCreating) {
            return;
        }
        setIsAddingFolder(true);
        setIsDuplicateError(false);
        setDuplicateErrorMessage(undefined);
        setNewFolderName('');
    };

    const handleCancelInlineAdd = () => {
        clearInlineAddState();
    };

    const handleSelectFolder = (folder: ReturnFolderDto) => {
        setSelectedFolder(folder);
    };

    const handleListSelectionChange = (_ev: React.SyntheticEvent | Event, data: { selectedItems: SelectionItemId[] }) => {
        const selectedKey = data.selectedItems[0];
        if (selectedKey == null) {
            return;
        }
        const folder = (folders ?? []).find((candidate) => String(candidate.folderKey) === String(selectedKey));
        if (folder) {
            setSelectedFolder(folder);
        }
    };

    const submitAndClearState = async () => {
        if (selectedFolder) {
            await addReturnsToExistingFolder({
                data: {
                    folderKey: selectedFolder.folderKey,
                    taxReturnItemKeys: selectedReturns,
                },
            });

            displayFeedbackMessage({
                type: 'success',
                message: (
                    <span>
                        {t('calculationsAddedToFolder')}
                        {selectedFolder.folderName}
                    </span>
                ),
            });
            clearStateAndClose();
        }
    };

    const createFolderAndAddReturns = () => {
        if (isCreating) {
            return;
        }

        saveNewFolderAndReturns({ data: { folderName: newFolderName, taxReturnItemKeys: selectedReturns } })
            .then((response: any) => {
                if (response?.errors?.length) {
                    if (response.errors.find((error: any) => error?.message?.includes('duplicate'))) {
                        setIsDuplicateError(true);
                        setDuplicateErrorMessage(`${t('folderExists').toString().replace('{{folderName}}', newFolderName)}`);
                    } else {
                        clearInlineAddState();
                        setErrorMessageBar(t('calculationAddToFolderServerError').toString());
                    }
                } else {
                    displayFeedbackMessage({
                        type: 'success',
                        message: (
                            <span>
                                {t('calculationsAddedToFolder').toString()}
                                {response?.folderName}
                            </span>
                        ),
                    });
                    queryClient.invalidateQueries(getAllFoldersQuery);
                    clearStateAndClose();
                }
            })
            .catch(() => {
                displayFeedbackMessage({
                    type: 'error',
                    message: `${t('calculationAddToFolderServerError').toString()}`,
                });
                queryClient.invalidateQueries(getAllFoldersQuery);
                clearStateAndClose();
            });
    };

    const handleNewFolderNameChange = (value: string) => {
        setNewFolderName(value);
        if (isDuplicateError) {
            setIsDuplicateError(false);
            setDuplicateErrorMessage(undefined);
        }
    };

    const listContent =
        !hasResults && !isAddingFolder ? (
            <div className={pickerStyles.noResults}>{t('noFoldersFound')}</div>
        ) : (
            <>
                {isAddingFolder ? (
                    <div className={styles.inlineAddRow}>
                        <FolderListRow
                            mode='edit'
                            id='new-folder'
                            value={newFolderName}
                            onChange={handleNewFolderNameChange}
                            onConfirm={createFolderAndAddReturns}
                            onCancel={handleCancelInlineAdd}
                            isProcessing={isCreating}
                            isError={isDuplicateError}
                            autoFocus
                            processingLabel={t('processing').toString()}
                            inputAriaLabel={t('enterFolderName').toString()}
                            confirmAriaLabel={t('confirmRenameFolder').toString()}
                            cancelAriaLabel={t('cancelRenameFolder').toString()}
                        />
                        {duplicateErrorMessage ? <div className={styles.duplicateError}>{duplicateErrorMessage}</div> : null}
                    </div>
                ) : null}
                <List
                    className={styles.list}
                    selectionMode='single'
                    selectedItems={selectedItems}
                    onSelectionChange={handleListSelectionChange}
                    aria-label={t('folderName').toString()}
                    aria-busy={isLoadingGetAllFolders}
                >
                    {visibleFolders.map((folder, index) => {
                        const isSelected = selectedFolder?.folderKey === folder.folderKey;
                        return (
                            <ListItem
                                key={folder.folderKey}
                                id={`item-${isAddingFolder ? index + 1 : index}`}
                                value={String(folder.folderKey)}
                                // Keep selectionMode semantics; hide default checkbox chrome.
                                checkmark={null}
                                className={mergeClasses(styles.listItem, isSelected && styles.listItemSelected)}
                            >
                                <FolderListRow
                                    mode='display'
                                    label={folder.folderName ?? ''}
                                    onSelect={() => handleSelectFolder(folder)}
                                    selectAriaLabel={folder.folderName ?? undefined}
                                    isSelected={isSelected}
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </>
        );

    const bodyContent = (
        <div className={styles.modalContentStyles}>
            <div className={styles.searchWrapper}>
                <FilterSearchBar
                    searchBoxPlaceholderText={t('search')}
                    onSearchBoxChange={(_ev, newValue) => setSearchValue(newValue ?? '')}
                    onAbort={() => setSearchValue('')}
                    shouldFocusOnFirstItemOnTab={!isAddingFolder}
                />
            </div>
            <div className={styles.listRegion}>
                {isLoadingGetAllFolders ? (
                    <div className={styles.loading}>
                        <Spinner size='small' />
                    </div>
                ) : (
                    listContent
                )}
            </div>
        </div>
    );

    const leftFooterElement = (
        <FunctionalAccessDisableWrapper hasFunctionalAccess={functionalAccess?.createFolderButtonEnabled}>
            <Button
                aria-label={t('addToNewFolder').toString()}
                id='add-to-new-folder-button'
                icon={<FolderArrowRight20Regular />}
                onClick={handleStartAddFolder}
                disabled={isAddingFolder || isCreating}
                disabledFocusable={isAddingFolder || isCreating}
            >
                {t('addToNewFolder').toString()}
            </Button>
        </FunctionalAccessDisableWrapper>
    );

    const modalButtons = (
        <>
            <Button
                appearance='primary'
                size='small'
                aria-label={t('add').toString()}
                disabled={selectedFolder === undefined || isSubmitting || isAddingFolder || isCreating}
                onClick={submitAndClearState}
                className={button}
            >
                {isSubmitting ? t('working').toString() : t('add').toString()}
                {isSubmitting && <Spinner size='extra-tiny' />}
            </Button>
            <Button size='small' aria-label={t('cancel').toString()} onClick={clearStateAndClose} className={button}>
                {t('cancel').toString()}
            </Button>
        </>
    );

    return (
        <Modal
            leftFooterElement={leftFooterElement}
            headerContent={headerContent}
            bodyContent={bodyContent}
            buttons={modalButtons}
            isOpen={isOpen}
            onClose={clearStateAndClose}
            messageBar={messageBar}
        />
    );
};

export default AddToFolderModal;
